import React, { useEffect, useState } from 'react';
import { messaging } from '../firebase/config';
import { getToken, onMessage } from 'firebase/messaging';
import './NotificationTest.css'; // Import CSS file

// Import icons (using emojis instead of lucide-react to avoid dependency)
const Bell = () => <span role="img" aria-label="bell">🔔</span>;
const Copy = () => <span role="img" aria-label="copy">📋</span>;
const CheckCircle = () => <span role="img" aria-label="check">✅</span>;
const AlertCircle = () => <span role="img" aria-label="alert">⚠️</span>;
const Smartphone = () => <span role="img" aria-label="smartphone">📱</span>;
const WifiOff = () => <span role="img" aria-label="wifi">📡</span>;
const Shield = () => <span role="img" aria-label="shield">🛡️</span>;
const RefreshCw = () => <span role="img" aria-label="refresh">🔄</span>;
const Eye = () => <span role="img" aria-label="eye">👁️</span>;

const NotificationTest = () => {
  const [token, setToken] = useState('Fetching token...');
  const [receivedMessage, setReceivedMessage] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isCopied, setIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [notificationHistory, setNotificationHistory] = useState([]);

  useEffect(() => {
    // Check initial permission status
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }

    const setupNotifications = async () => {
      setIsLoading(true);
      try {
        const permission = await Notification.requestPermission();
        setPermissionStatus(permission);
        
        if (permission === 'granted') {
          const currentToken = await getToken(messaging, { 
            vapidKey: 'BOArGppOCzIOr4oiGDQcaXJEEiup62RhexztLW6dbYO6278bajUULiwDpsi9V8ywIlaPL174K46F2qgWUQCYagI'
          });
          
          if (currentToken) {
            setToken(currentToken);
            // Store token in localStorage for persistence
            localStorage.setItem('fcmToken', currentToken);
          } else {
            setToken("Token not available");
          }
        } else {
          setToken("Notification permission denied");
        }
      } catch (err) {
        console.error('Error setting up notifications:', err);
        setToken("Error: " + err.message);
      } finally {
        setIsLoading(false);
      }
    };

    setupNotifications();

    // Listen for foreground messages
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('New message (Foreground):', payload);
      
      const newNotification = {
        ...payload.notification,
        timestamp: new Date().toLocaleTimeString(),
        id: Date.now(),
        type: 'foreground'
      };
      
      setReceivedMessage(payload.notification);
      setNotificationHistory(prev => [newNotification, ...prev.slice(0, 4)]);
    });

    return () => unsubscribe();
  }, []);

  const copyToken = () => {
    navigator.clipboard.writeText(token);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const requestPermission = async () => {
    setIsLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        const currentToken = await getToken(messaging, { 
          vapidKey: 'BOArGppOCzIOr4oiGDQcaXJEEiup62RhexztLW6dbYO6278bajUULiwDpsi9V8ywIlaPL174K46F2qgWUQCYagI'
        });
        if (currentToken) {
          setToken(currentToken);
        }
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearNotification = () => {
    setReceivedMessage(null);
  };

  const PermissionStatusBadge = ({ status }) => {
    const config = {
      granted: { 
        color: 'permission-badge granted', 
        icon: <CheckCircle />, 
        text: 'Granted' 
      },
      denied: { 
        color: 'permission-badge denied', 
        icon: <AlertCircle />, 
        text: 'Denied' 
      },
      default: { 
        color: 'permission-badge default', 
        icon: <Shield />, 
        text: 'Default' 
      }
    };
    
    const { color, icon, text } = config[status] || config.default;
    
    return (
      <div className={color}>
        {icon}
        <span>{text}</span>
      </div>
    );
  };

  return (
    <div className="notification-test-container">
      <div className="notification-test-wrapper">
        {/* Header */}
        <header className="notification-header">
          <div className="header-icon">
            <Bell />
          </div>
          <h1 className="notification-title">
            Push Notification Tester
          </h1>
          <p className="notification-subtitle">
            Test and debug your Firebase Cloud Messaging implementation
          </p>
        </header>

        <div className="notification-grid">
          {/* Left Column - Token & Permission */}
          <div className="notification-left-column">
            {/* Permission Status Card */}
            <div className="notification-card">
              <div className="permission-header">
                <div className="permission-info">
                  <h2 className="card-title">
                    <Shield />
                    Notification Permission
                  </h2>
                  <p className="card-description">
                    Current browser notification status
                  </p>
                </div>
                <PermissionStatusBadge status={permissionStatus} />
              </div>
              
              <button
                onClick={requestPermission}
                disabled={isLoading || permissionStatus === 'granted'}
                className={`permission-button ${permissionStatus === 'granted' ? 'granted' : ''} ${isLoading ? 'loading' : ''}`}
              >
                {isLoading ? (
                  <>
                    <RefreshCw />
                    Processing...
                  </>
                ) : permissionStatus === 'granted' ? (
                  <>
                    <CheckCircle />
                    Permission Granted
                  </>
                ) : (
                  <>
                    <Bell />
                    {permissionStatus === 'denied' ? 'Reset Permission' : 'Allow Notifications'}
                  </>
                )}
              </button>
            </div>

            {/* Device Token Card */}
            <div className="notification-card">
              <div className="token-header">
                <h2 className="card-title">
                  <Smartphone />
                  Device Token
                </h2>
                <span className="token-status">
                  {isLoading ? 'Fetching...' : 'Ready'}
                </span>
              </div>
              
              <p className="card-description">
                Use this token to send test notifications from Firebase Console
              </p>
              
              <div className="token-container">
                <div className="token-display">
                  {isLoading ? (
                    <div className="loading-token">
                      <RefreshCw />
                      Fetching device token...
                    </div>
                  ) : (
                    <code className="token-text">
                      {token}
                    </code>
                  )}
                </div>
                
                <button
                  onClick={copyToken}
                  disabled={!token || token.includes('Error') || token.includes('denied')}
                  className={`copy-button ${isCopied ? 'copied' : ''} ${!token || token.includes('Error') || token.includes('denied') ? 'disabled' : ''}`}
                >
                  {isCopied ? (
                    <>
                      <CheckCircle />
                      Copied to Clipboard!
                    </>
                  ) : (
                    <>
                      <Copy />
                      Copy Token
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Live Preview & Instructions */}
          <div className="notification-right-column">
            {/* Live Preview Card */}
            <div className="notification-card">
              <h2 className="card-title">
                <Eye />
                Live Preview
              </h2>
              
              {receivedMessage ? (
                <div className="preview-container">
                  <div className="notification-preview">
                    <div className="preview-content">
                      <div className="notification-icon">
                        <Bell />
                      </div>
                      <div className="preview-details">
                        <div className="preview-header">
                          <h3 className="preview-title">{receivedMessage.title}</h3>
                          <span className="preview-time">
                            Just now
                          </span>
                        </div>
                        <p className="preview-body">{receivedMessage.body}</p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={clearNotification}
                    className="clear-button"
                  >
                    Clear Preview
                  </button>
                </div>
              ) : (
                <div className="empty-preview">
                  <div className="empty-icon">
                    <Bell />
                  </div>
                  <h3 className="empty-title">No Active Preview</h3>
                  <p className="empty-description">
                    Send a test notification to see it here
                  </p>
                </div>
              )}

              {/* Notification History */}
              {notificationHistory.length > 0 && (
                <div className="notification-history">
                  <h3 className="history-title">Recent Notifications</h3>
                  <div className="history-list">
                    {notificationHistory.map(notification => (
                      <div key={notification.id} className="history-item">
                        <div className="history-item-header">
                          <span className="history-item-title">{notification.title}</span>
                          <span className="history-item-time">{notification.timestamp}</span>
                        </div>
                        <p className="history-item-body">{notification.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Instructions Card */}
            <div className="notification-card instructions-card">
              <h2 className="card-title">
                <WifiOff />
                Testing Guide
              </h2>
              
              <div className="instructions-list">
                <div className="instruction-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4 className="step-title">Copy Token</h4>
                    <p className="step-description">
                      Copy your unique device token above
                    </p>
                  </div>
                </div>
                
                <div className="instruction-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4 className="step-title">Send Test Notification</h4>
                    <p className="step-description">
                      Use Firebase Console to send a test message
                    </p>
                  </div>
                </div>
                
                <div className="instruction-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4 className="step-title">Check Both States</h4>
                    <p className="step-description">
                      Test with app open (foreground) and closed (background)
                    </p>
                  </div>
                </div>
                
                <div className="instruction-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4 className="step-title">Verify Delivery</h4>
                    <p className="step-description">
                      Check notifications appear in both states
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="pro-tip">
                <p className="pro-tip-text">
                  <span className="pro-tip-label">Pro Tip:</span> For background testing, 
                  minimize the app or switch tabs before sending the notification.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Footer */}
        <footer className="notification-footer">
          <div className="status-indicators">
            <div className="status-indicator">
              <div className={`status-dot ${permissionStatus === 'granted' ? 'granted' : 'warning'}`}></div>
              <span>Permission: {permissionStatus}</span>
            </div>
            <div className="status-indicator">
              <div className={`status-dot ${token && !token.includes('Error') && !token.includes('denied') ? 'granted' : 'error'}`}></div>
              <span>Token: {isLoading ? 'Loading...' : token && !token.includes('Error') ? 'Available' : 'Unavailable'}</span>
            </div>
            <div className="status-indicator">
              <div className="status-dot connected"></div>
              <span>Firebase: Connected</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default NotificationTest;